export const SUGGEST_STATUS_BAR_HEIGHT = 36;

const formatSuggestStatusBarKeyboardShortcuts = () => {
    document
        .querySelectorAll<HTMLElement>('.monaco-editor .suggest-widget .suggest-status-bar .action-label')
        .forEach((actionLabel) => {
            if (actionLabel.querySelector('.vantage-suggest-shortcut-tag')) {return;}

            const labelText = actionLabel.textContent?.trim() ?? '';
            const keyboardShortcutMatch = labelText.match(/^(.+?)\s*\(([^)]+)\)$/);

            if (!keyboardShortcutMatch) {return;}

            const [, action, keyboardShortcut] = keyboardShortcutMatch;
            const keyboardShortcutTag = document.createElement('small');

            keyboardShortcutTag.className = 'vantage-suggest-shortcut-tag';
            keyboardShortcutTag.textContent = keyboardShortcut.trim();
            actionLabel.textContent = '';
            actionLabel.append(document.createTextNode(action.trim()));
            actionLabel.append(keyboardShortcutTag);
        });
};

const getSuggestRowSectionLabel = (row: HTMLElement) => {
    const dataType = row.querySelector<HTMLElement>('.details-label')?.textContent?.trim().toLowerCase();
    const icon = row.querySelector<HTMLElement>('.suggest-icon');

    if (dataType === 'template') {return 'Templates';}
    if (dataType === 'sql keyword') {return 'SQL Keywords';}
    if (icon?.classList.contains('codicon-symbol-variable')) {return 'Dynamic Variables';}
    if (icon?.classList.contains('codicon-symbol-class')) {return 'Tables';}
    if (icon?.classList.contains('codicon-symbol-field')) {return 'Columns';}
    if (icon?.classList.contains('codicon-symbol-module')) {return 'Projections';}

    return null;
};

const getSuggestRowTop = (row: HTMLElement) => {
    const top = Number.parseFloat(row.style.top);

    return Number.isNaN(top) ? row.getBoundingClientRect().top : top;
};

const formatSuggestSectionHeaders = () => {
    const sectionLabels = new Set<string>();
    const rows = Array.from(
        document.querySelectorAll<HTMLElement>('.monaco-editor .suggest-widget .monaco-list .monaco-list-row'),
    ).sort((a, b) => getSuggestRowTop(a) - getSuggestRowTop(b));

    rows.forEach((row) => {
        row.classList.remove('vantage-suggest-section-start');
        row.removeAttribute('data-vantage-section');
        row.querySelector('.vantage-suggest-section-label')?.remove();
    });

    rows.forEach((row) => {
        const sectionLabel = getSuggestRowSectionLabel(row);

        if (!sectionLabel || sectionLabels.has(sectionLabel)) {return;}

        const sectionLabelElement = document.createElement('small');

        sectionLabelElement.className = 'vantage-suggest-section-label';
        sectionLabelElement.textContent = sectionLabel;
        sectionLabels.add(sectionLabel);
        row.classList.add('vantage-suggest-section-start');
        row.dataset.vantageSection = sectionLabel;
        row.prepend(sectionLabelElement);
    });
};

const syncSuggestWidgetFooterHeight = (footerHeightAdjustment: number) => {
    if (footerHeightAdjustment <= 0) {return;}

    document.querySelectorAll<HTMLElement>('.monaco-editor .suggest-widget.with-status-bar').forEach((widget) => {
        const styleHeight = Number.parseFloat(widget.style.height);
        const adjustedHeight = Number.parseFloat(widget.dataset.vantageSuggestAdjustedHeight ?? '');

        if (Number.isNaN(styleHeight) || styleHeight <= footerHeightAdjustment) {return;}
        if (!Number.isNaN(adjustedHeight) && Math.abs(styleHeight - adjustedHeight) < 0.5) {return;}

        const nextHeight = Math.max(SUGGEST_STATUS_BAR_HEIGHT, styleHeight - footerHeightAdjustment);

        widget.style.height = `${nextHeight}px`;
        widget.dataset.vantageSuggestAdjustedHeight = `${nextHeight}`;
    });
};

const syncSuggestEmptyView = () => {
    let activeEmptyViewTarget: HTMLElement | null = null;

    document.querySelectorAll<HTMLElement>('.monaco-editor .suggest-widget').forEach((widget) => {
        const message = widget.querySelector<HTMLElement>(':scope > .message');
        const existingTarget = message?.querySelector<HTMLElement>('.vantage-suggest-empty-view') ?? null;
        const isEmpty =
            Boolean(message) &&
            widget.classList.contains('message') &&
            (message?.dataset.vantageEmptySuggestions === 'true' ||
                message?.textContent?.trim().toLowerCase() === 'no suggestions.');

        if (!message || !isEmpty) {
            existingTarget?.remove();
            message?.removeAttribute('data-vantage-empty-suggestions');
            message?.removeAttribute('aria-label');
            widget.classList.remove('vantage-suggest-empty-state');
            return;
        }

        let target = existingTarget;

        if (!target) {
            target = document.createElement('div');
            target.className = 'vantage-suggest-empty-view';
            message.textContent = '';
            message.append(target);
        }

        message.dataset.vantageEmptySuggestions = 'true';
        message.setAttribute('aria-label', 'No suggestions available.');
        widget.classList.add('vantage-suggest-empty-state');

        if (!activeEmptyViewTarget || widget.classList.contains('visible')) {
            activeEmptyViewTarget = target;
        }
    });

    return activeEmptyViewTarget;
};

export const formatSuggestWidgetDom = (footerHeightAdjustment: number) => {
    formatSuggestStatusBarKeyboardShortcuts();
    formatSuggestSectionHeaders();
    syncSuggestWidgetFooterHeight(footerHeightAdjustment);

    return syncSuggestEmptyView();
};
